import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TOOLTIPS
 *
 * `@radix-ui/react-tooltip` has been a dependency all along with nothing
 * using it. The icon-only buttons carried `aria-label`, which a screen reader
 * announces and a mouse user never hears — so a row of four unlabelled icons
 * was a guess for everybody who can see.
 *
 * ── Why not the native `title` attribute ──────────────────────────────────
 * It works, and it is what an icon button usually gets. But the browser waits
 * about a second before showing it, renders it in the OS style rather than
 * the product's, and never appears at all for a keyboard user tabbing through
 * the row. Radix opens on focus as well as hover, which is the case that
 * matters most for a control with no text on it.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background shadow-md',
        'animate-in fade-in-0 zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * An icon button's label, in one element.
 *
 * The four-part Radix dance around every icon is enough friction that the next
 * icon button will simply not get one — which is how the row ended up
 * unlabelled in the first place. `delayDuration={200}` because these sit in a
 * row: the default 700ms means moving along four icons shows nothing at all.
 */
export function IconTooltip({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
